import "@/App.css";
import { useEffect, useRef, lazy, Suspense } from "react";
import { CookieProvider } from "@/components/CookieConsent";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import { Analytics } from "@/components/Analytics";

// Efecto de cursor solo en dispositivos con puntero fino (escritorio); en móvil no se carga su chunk (ni framer-motion).
const CAN_HOVER = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: fine)").matches;

import Home from "@/pages/Home";
const CursorTrail = lazy(() => import("@/components/CursorTrail"));
const Historias = lazy(() => import("@/pages/Historias"));
const HistoriaDetail = lazy(() => import("@/pages/HistoriaDetail"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const Playlists = lazy(() => import("@/pages/Playlists"));
const PlaylistDetail = lazy(() => import("@/pages/PlaylistDetail"));
const QuienesSomos = lazy(() => import("@/pages/QuienesSomos"));
const Contacto = lazy(() => import("@/pages/Contacto"));
const Canciones = lazy(() => import("@/pages/Canciones"));
const CancionDetail = lazy(() => import("@/pages/CancionDetail"));
const RadarPage = lazy(() => import("@/pages/Radar"));
const RadarArtist = lazy(() => import("@/pages/RadarArtist"));
const SalaDetail = lazy(() => import("@/pages/SalaDetail"));
const Buscar = lazy(() => import("@/pages/Buscar"));
const PoliticaPrivacidad = lazy(() => import("@/pages/PoliticaPrivacidad"));
const PoliticaCookies = lazy(() => import("@/pages/PoliticaCookies"));
const Admin = lazy(() => import("@/pages/Admin"));
const Baja = lazy(() => import("@/pages/Baja"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function LegacyNewsRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/noticias/${slug}`} replace />;
}

function LegacyPlaylistRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/playlists/${slug}`} replace />;
}

function useScrollToTop(pathname) {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
}

function AnimatedRoutes() {
  const location = useLocation();
  useScrollToTop(location.pathname);
  const firstLoad = useRef(true);
  const skipInitial = firstLoad.current;
  firstLoad.current = false;
  return (
    <div key={location.pathname} className={skipInitial ? undefined : "anim-route"}>
        <Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>
        <Routes location={location}>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="noticias" element={<Historias />} />
            <Route path="noticias/:slug" element={<HistoriaDetail />} />
            <Route path="ultimas-noticias" element={<Navigate to="/noticias" replace />} />
            <Route path="ultimas-noticias/:slug" element={<LegacyNewsRedirect />} />
            <Route path="historias" element={<Navigate to="/noticias" replace />} />
            <Route path="historias/:slug" element={<LegacyNewsRedirect />} />
            <Route path="agenda" element={<Agenda />} />
            <Route path="agenda/sala/:slug" element={<SalaDetail />} />
            <Route path="playlists" element={<Playlists />} />
            <Route path="playlists/:slug" element={<PlaylistDetail />} />
            <Route path="quienes-somos" element={<QuienesSomos />} />
            <Route path="contacto" element={<Contacto />} />
            <Route path="colecciones" element={<Navigate to="/playlists" replace />} />
            <Route path="colecciones/:slug" element={<LegacyPlaylistRedirect />} />
            <Route path="canciones" element={<Canciones />} />
            <Route path="canciones/:slug" element={<CancionDetail />} />
            <Route path="radar" element={<RadarPage />} />
            <Route path="radar/:slug" element={<RadarArtist />} />
            <Route path="refugio" element={<Navigate to="/" replace />} />
            <Route path="buscar" element={<Buscar />} />
            <Route path="politica-de-privacidad" element={<PoliticaPrivacidad />} />
            <Route path="politica-de-cookies" element={<PoliticaCookies />} />
            <Route path="mazuecos" element={<Admin />} />
            <Route path="baja" element={<Baja />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        </Suspense>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <html lang="es" />
        <meta property="og:site_name" content="La Estrofa" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="App grain min-h-screen">
        {CAN_HOVER && <Suspense fallback={null}><CursorTrail /></Suspense>}
        <BrowserRouter>
          <CookieProvider>
            <Analytics />
            <AnimatedRoutes />
            <Toaster position="bottom-right" richColors />
          </CookieProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}
